import { useState } from "react";
import { Button } from "../ui/button";
import { Link } from "react-router-dom";
import { SERVICES } from "../../lib/site-data";
import { ArrowRight } from "lucide-react";

const STEPS = [
    {
        q: "Sizi en çok ilgilendiren konu hangisi?",
        options: [
            { label: "Mevcut bir dövmeyi silmek istiyorum", value: "tattoo" },
            { label: "Yeni dövme öncesi açtırmak istiyorum", value: "coverup" },
            { label: "Bir iz görünümünü azaltmak istiyorum", value: "scar" },
            { label: "Cilt yenileme / akne izi", value: "skin" },
        ],
    },
    {
        q: "Daha fazla detay verir misiniz?",
        cond: { tattoo: ["color", "size"], coverup: ["coverDetail"], scar: ["scarType"], skin: ["skinFocus"] },
    },
];

const RESULT_MAP = {
    tattoo: "pico-lazer-dovme-silme",
    coverup: "cover-up-oncesi-dovme-actirma",
    scarBurn: "yanik-izi-tedavisi",
    scarFaca: "faca-izi-tedavisi",
    scarSurgery: "ameliyat-izi-tedavisi",
    skinAcne: "akne-izi-tedavisi",
    skinCo2: "fraksiyonel-co2-lazer",
};

export default function Quiz() {
    const [step1, setStep1] = useState(null);
    const [result, setResult] = useState(null);

    const reset = () => {
        setStep1(null);
        setResult(null);
    };

    const renderStep2 = () => {
        if (step1 === "tattoo")
            return [
                { label: "Siyah / koyu mürekkep", v: RESULT_MAP.tattoo },
                { label: "Renkli dövme (kırmızı/mavi/yeşil)", v: RESULT_MAP.tattoo },
                { label: "Cover-up için açtırmak istiyorum", v: RESULT_MAP.coverup },
            ];
        if (step1 === "coverup")
            return [
                { label: "Dövmemi yeni tasarım için açtıracağım", v: RESULT_MAP.coverup },
                { label: "Tamamen silmek istiyorum", v: RESULT_MAP.tattoo },
            ];
        if (step1 === "scar")
            return [
                { label: "Yanık izi", v: RESULT_MAP.scarBurn },
                { label: "Faça / kesik izi", v: RESULT_MAP.scarFaca },
                { label: "Ameliyat / travma izi", v: RESULT_MAP.scarSurgery },
            ];
        if (step1 === "skin")
            return [
                { label: "Akne izi görünümü", v: RESULT_MAP.skinAcne },
                { label: "Cilt yüzeyi pürüzlülüğü / yenileme", v: RESULT_MAP.skinCo2 },
            ];
        return [];
    };

    const slug = result;
    const recommended = SERVICES.find((s) => s.slug === slug);

    return (
        <section className="relative py-24 md:py-32 border-b border-white/5" data-testid="quiz-section">
            <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">
                <div className="text-center mb-12">
                    <p className="text-xs uppercase tracking-[0.3em] text-cyan-300/80 mb-3">
                        İnteraktif
                    </p>
                    <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight max-w-2xl mx-auto text-balance">
                        Hangi hizmet size uygun olabilir?
                    </h2>
                    <p className="text-sm text-zinc-400 mt-4 max-w-xl mx-auto">
                        Aşağıdaki kısa rehber yalnızca yönlendirme amaçlıdır. Kesin uygunluk
                        kişisel değerlendirme sonrası belirlenir.
                    </p>
                </div>

                <div className="bg-[#0E0E0E] border border-white/5 rounded-2xl p-8 md:p-12 relative overflow-hidden">
                    <div className="absolute -top-20 -left-20 w-60 h-60 bg-cyan-400/10 blur-[80px] rounded-full pointer-events-none"></div>

                    {!step1 && !result && (
                        <div className="relative space-y-5">
                            <p className="text-lg text-zinc-200 mb-6">{STEPS[0].q}</p>
                            <div className="grid sm:grid-cols-2 gap-3">
                                {STEPS[0].options.map((opt) => (
                                    <button
                                        key={opt.value}
                                        onClick={() => setStep1(opt.value)}
                                        className="text-left p-5 bg-[#141414] border border-white/5 rounded-xl hover:border-cyan-400/40 hover:bg-[#181818] transition-all"
                                        data-testid={`quiz-step1-${opt.value}`}
                                    >
                                        <span className="text-sm text-white">{opt.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {step1 && !result && (
                        <div className="relative space-y-5 animate-slide-in">
                            <p className="text-lg text-zinc-200 mb-6">Detayı seçin:</p>
                            <div className="grid sm:grid-cols-2 gap-3">
                                {renderStep2().map((opt, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setResult(opt.v)}
                                        className="text-left p-5 bg-[#141414] border border-white/5 rounded-xl hover:border-cyan-400/40 hover:bg-[#181818] transition-all"
                                        data-testid={`quiz-step2-${i}`}
                                    >
                                        <span className="text-sm text-white">{opt.label}</span>
                                    </button>
                                ))}
                            </div>
                            <button
                                onClick={reset}
                                className="text-xs text-zinc-500 hover:text-zinc-300"
                            >
                                ← Baştan başla
                            </button>
                        </div>
                    )}

                    {result && recommended && (
                        <div className="relative animate-slide-in" data-testid="quiz-result">
                            <p className="text-xs uppercase tracking-[0.3em] text-cyan-300/80 mb-3">
                                Önerilen Yaklaşım
                            </p>
                            <h3 className="font-display text-3xl md:text-4xl text-white mb-3">
                                {recommended.title}
                            </h3>
                            <p className="text-base text-zinc-400 mb-6 max-w-2xl">
                                {recommended.intro}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <Link to={`/${recommended.slug}`}>
                                    <Button className="bg-cyan-400 text-black hover:bg-cyan-300 font-medium">
                                        Hizmet Detayını İncele
                                        <ArrowRight className="h-4 w-4 ml-2" />
                                    </Button>
                                </Link>
                                <a href="#lead-form">
                                    <Button
                                        variant="outline"
                                        className="bg-transparent border-white/20 text-white hover:bg-white/5"
                                    >
                                        Ön Değerlendirme Formu
                                    </Button>
                                </a>
                                <button
                                    onClick={reset}
                                    className="text-xs text-zinc-500 hover:text-zinc-300 self-center"
                                >
                                    ← Baştan başla
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
